import { inject, Injectable } from '@angular/core';
import { CrmEndpoint, CrmEndpointDecorator } from 'common-module/endpoint';
import {
  catchError,
  forkJoin,
  map,
  mergeMap,
  Observable,
  of,
  OperatorFunction,
  tap,
  throwError,
} from 'rxjs';
import { CrmEnvelope } from 'common-module/signature';
import { MediaService } from '@app/core/media/media.service';
import { CrmPaginated } from 'common-module/table';
import { NzUploadFile } from 'ng-zorro-antd/upload';
import { SignatureService } from '@app/pages/pl-cards/signature.service';
import { CrmMessageService } from 'common-module/message';
import { Media } from '@app/core/model/document-media';

@Injectable({ providedIn: 'root' })
export class EnvelopeService {
  @CrmEndpointDecorator({
    configName: 'crm',
    endpointName: 'envelopes',
  })
  private endpoint!: CrmEndpoint<any>;
  private signerService = inject(SignatureService);
  private mediaService = inject(MediaService);
  private messageService = inject(CrmMessageService);

  getEnvelope(id: string) {
    return this.endpoint
      .read(id)
      .pipe(
        mergeMap((envlp: CrmEnvelope) =>
          this.populateDocumentMedia([envlp]).pipe(map((res) => res[0])),
        ),
      );
  }

  listEnvelopes(params?: any) {
    return (
      this.endpoint.list({
        params: {
          '$sort[createdAt]': -1,
          ...params,
        },
      }) as Observable<CrmPaginated<any>>
    ).pipe(
      mergeMap(({ data, ...pag }: CrmPaginated<CrmEnvelope>) =>
        this.populateDocumentMedia(data).pipe(
          this.populateDocumentAuthor(),
          map((res) => ({ data: res, ...pag })),
        ),
      ),
    );
  }

  createEnvelope(
    products: string[],
    file: NzUploadFile,
    type: 'plcard' | 'pricelist',
  ) {
    // 1. Upload media with product.id as ref
    return this.mediaService
      .uploadDocument(file, {
        type,
        products: products.join(','),
      })
      .pipe(
        // 2. Create envelope with uploaded media as ref
        mergeMap((res: Media) => {
          return this.endpoint
            .create({
              name: file.name,
              ref: res._id,
            })
            .pipe(
              catchError((e) => {
                return throwError(() => e);
              }),
            );
        }),
        // 3. Sign created envelope
        mergeMap((envlp: CrmEnvelope) =>
          this.signerService.signEnvelope(envlp._id, {}).pipe(
            catchError((err) => {
              if (err.status === 400) {
                this.messageService.error('error.envelope.documentSignFailed');
                return of(null);
              }
              return throwError(err);
            }),
          ),
        ),
      );
  }

  pushExistingPlCard(productIds: string[], mediaId: string) {
    return this.mediaService.updateMedia(mediaId, {
      $push: {
        'meta.plcardProducts': {
          $each: productIds,
        },
      },
    });
  }

  removeEnvelope(id: string) {
    return this.endpoint.update(id, { status: 'voided' });
  }

  private populateDocumentMedia(
    forEnvelopes: CrmEnvelope[],
  ): Observable<CrmEnvelope[]> {
    const mediaObs = forEnvelopes.map((env) =>
      this.mediaService.get(env.ref).pipe(
        catchError(() => of(null)),
        map((doc: any) => ({ ...env, document: doc ?? null }) as CrmEnvelope),
      ),
    );
    return mediaObs.length ? forkJoin(mediaObs) : of(forEnvelopes);
  }

  private populateDocumentAuthor(): OperatorFunction<
    CrmEnvelope[],
    CrmEnvelope[]
  > {
    return (source: Observable<CrmEnvelope[]>) =>
      source.pipe(
        map((envelopes) =>
          envelopes.map((envlp) => ({
            ...envlp,
            document: {
              ...envlp.document,
              author: envlp.meta?.['authorName'],
            },
          })),
        ),
      );
  }
}
